/**
 * Human-friendly relative time for social feeds.
 * e.g. "just now", "5 minutes ago", "1 hour ago", "2 days ago", "5 months ago"
 */
export function formatRelativeTime(value, now = Date.now()) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const diffMs = Math.max(0, now - d.getTime());
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 45) return "just now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) {
    return minutes === 1 ? "1 minute ago" : `${minutes} minutes ago`;
  }

  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  }

  const days = Math.floor(hours / 24);
  if (days < 30) {
    return days === 1 ? "1 day ago" : `${days} days ago`;
  }

  const months = Math.floor(days / 30);
  if (months < 12) {
    return months === 1 ? "1 month ago" : `${months} months ago`;
  }

  const years = Math.floor(days / 365);
  return years === 1 ? "1 year ago" : `${years} years ago`;
}

/**
 * Compact relative age for timeline rows (Threads-style): 5m, 18h, 2d, 3mo, 1y.
 */
export function formatCompactRelativeTime(value, now = Date.now()) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";

  const diffMs = Math.max(0, now - d.getTime());
  const seconds = Math.floor(diffMs / 1000);
  if (seconds < 45) return "now";

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo`;

  const years = Math.floor(days / 365);
  return `${years}y`;
}

/**
 * Absolute locale date (workplace default).
 */
export function formatAbsoluteDate(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

/**
 * Absolute locale datetime (workplace thread default).
 */
export function formatAbsoluteDateTime(value) {
  if (!value) return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

/**
 * Pick relative vs absolute date for feed cards.
 * @param {{ relative?: boolean, compact?: boolean }} opts
 */
export function formatPostTimestamp(
  value,
  { relative = false, compact = false } = {}
) {
  if (relative) {
    return compact
      ? formatCompactRelativeTime(value)
      : formatRelativeTime(value);
  }
  return formatAbsoluteDate(value);
}

/**
 * Pick relative vs absolute datetime for threads/comments.
 * @param {{ relative?: boolean, compact?: boolean }} opts
 */
export function formatPostDateTime(
  value,
  { relative = false, compact = false } = {}
) {
  if (relative) {
    return compact
      ? formatCompactRelativeTime(value)
      : formatRelativeTime(value);
  }
  return formatAbsoluteDateTime(value);
}
